import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { hoursToMinutes } from 'date-fns';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';
import { Customers } from 'server/shared/collections/customers';
import { Users } from 'server/shared/collections/users';
import { randomId } from 'server/shared/helpers/random';
import { getNewUnixTimestamp } from 'server/shared/helpers/removeTimeZoneOffset';
import {
  formatTimeNumber,
  getTimeTrackingTotal,
  minutesToMilitaryTime,
} from 'server/shared/helpers/time';
import { Customer } from 'server/shared/models/customer';
import { Invitee } from 'server/shared/models/invitee';
import { Job } from 'server/shared/models/job';
import { Shift } from 'server/shared/models/shift';
import {
  ExternalLinkValidatorData,
  TimeTracking,
} from 'server/shared/models/timeTracking';
import { User } from 'server/shared/models/user';
import { customValidator } from 'src/helpers/timeTrackingHelper';
import { PermissionsService } from '../../services/permissions.service';
import { TimeTrackingService } from '../../services/time-tracking.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import { ConfirmationDialog } from '../confirmation-dialog/confirmation.component';

export type TimeTrackingWithFormGroup = {
  id: string;
  timeTracking: TimeTracking;
  formGroup: FormGroup;
};
@Component({
  selector: 'app-time-tracking',
  templateUrl: 'time-tracking.component.html',
  styleUrls: ['time-tracking.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeTrackingComponent implements OnChanges, OnDestroy {
  private destroyed$ = new Subject<void>();

  // Inputs
  invitee$ = new BehaviorSubject<Invitee | undefined>(undefined);
  @Input() set invitee(value: Invitee | undefined) {
    this.invitee$.next(value);
  }
  get invitee() {
    return this.invitee$.value;
  }

  job$ = new BehaviorSubject<Job | undefined>(undefined);
  @Input() set job(value: Job | undefined) {
    this.job$.next(value);
  }
  get job() {
    return this.job$.value;
  }

  isPooler$ = new BehaviorSubject<boolean>(false);
  @Input() set isPooler(value: boolean) {
    this.isPooler$.next(value);
  }
  get isPooler() {
    return this.isPooler$.value;
  }

  @Input() magicSecret?: string;

  isMagicLink$ = new BehaviorSubject<boolean>(false);
  @Input()
  get isMagicLink() {
    return this.isMagicLink$.value;
  }
  set isMagicLink(value: BooleanInput) {
    this.isMagicLink$.next(coerceBooleanProperty(value));
  }

  @Output() timeTrackingSubmitted = new EventEmitter();

  timeTrackingsFormArray$ = new BehaviorSubject<FormArray>(new FormArray([]));
  get timeTrackingsFormArray() {
    return this.timeTrackingsFormArray$.value;
  }

  timeTrackingsWithFormGroup$ = new BehaviorSubject<
    TimeTrackingWithFormGroup[]
  >([]);
  get timeTrackingsWithFormGroup() {
    return this.timeTrackingsWithFormGroup$.value;
  }
  timeTrackings: TimeTracking[] = [];

  timeTrackingsWithFormGroupForStepper$ = this.timeTrackingsWithFormGroup$.pipe(
    map((timeTrackingsWithFormGroup) => {
      let timeTrackings: TimeTrackingWithFormGroup[] =
        timeTrackingsWithFormGroup.map((item) => ({
          ...item,
          formGroup: new FormGroup({
            from: new FormControl(item.formGroup.controls['from'].value),
            to: new FormControl(item.formGroup.controls['to'].value),
            pause: new FormControl(item.formGroup.controls['pause'].value),
            correctionReason: new FormControl(
              item.formGroup.controls['correctionReason'].value,
            ),
          }),
        }));
      return timeTrackings;
    }),
    shareReplay(1),
  );

  customer$ = new BehaviorSubject<Customer | undefined>(undefined);
  get customer() {
    return this.customer$.value;
  }

  loggedInUser?: User;
  hasChanged: boolean = false;

  i18n = this.translationService.getTranslations('time-tracking');

  previousName?: string;

  timeTrackingTotal$ = new BehaviorSubject<number>(0);
  get timeTrackingTotal() {
    return this.timeTrackingTotal$.value;
  }

  // Computed property for canSubmitTimeTrackings
  canSubmitTimeTrackings$ = combineLatest([
    this.isPooler$,
    this.timeTrackingsWithFormGroup$,
  ]).pipe(
    map(([isPooler, timeTrackingsWithFormGroup]) => {
      return (
        (isPooler &&
          !timeTrackingsWithFormGroup.some(
            (tracking) => !!tracking.timeTracking.toBeApprovedAt,
          )) ||
        (!isPooler &&
          !timeTrackingsWithFormGroup.some(
            (tracking) => !!tracking.timeTracking.approvedAt,
          ))
      );
    }),
  );

  canOpenSigningStepper$ = combineLatest([
    this.isPooler$,
    this.timeTrackingsWithFormGroup$,
  ]).pipe(
    map(([isPooler, timeTrackingsWithFormGroup]) => {
      if (!isPooler) {
        return false;
      }
      const hasNotSubmitted = !timeTrackingsWithFormGroup.some(
        (tracking) => !!tracking.timeTracking.toBeApprovedAt,
      );
      const needsSignature = timeTrackingsWithFormGroup.some(
        (tracking) =>
          !!tracking.timeTracking.toBeApprovedAt &&
          tracking.timeTracking.toBeApprovedAt >=
            getNewUnixTimestamp() - 3600 &&
          !tracking.timeTracking.signatureFile &&
          tracking.timeTracking.signatureProcessEnabled,
      );
      return hasNotSubmitted || needsSignature;
    }),
  );
  signingStepperOpen = false;

  hasPermissions$ = this.permissionsService.completePermissions$.pipe(
    map((completePermissions) => {
      return completePermissions.dispoAccess.featurePermissions.canApproveHours;
    }),
    startWith(false),
  );

  canApproveHours$ = combineLatest([
    this.isPooler$,
    this.hasPermissions$,
    this.isMagicLink$,
  ]).pipe(
    map(([isPooler, hasPermissions, isMagicLink]) => {
      // Poolers can always enter their hours
      if (isPooler) {
        return true;
      }
      // Dispatchers need canApproveHours permission
      return hasPermissions || isMagicLink;
    }),
    shareReplay(1),
  );

  constructor(
    private ngZone: NgZone,
    private translationService: TranslationService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private permissionsService: PermissionsService,
    private timeTrackingService: TimeTrackingService,
  ) {}

  private trackerComputation?: Tracker.Computation;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['invitee'] && this.invitee) {
      this.setTimeTrackings(this.invitee);
    }

    if (!this.trackerComputation) {
      this.trackerComputation = Tracker.autorun(() => {
        this.loggedInUser = Users.findOne(Meteor.userId());
        const customer = Customers.findOne(this.invitee?.customer.id);
        this.ngZone.run(() => {
          this.customer$.next(customer);
        });
      });
    }

    if (changes['isMagicLink'] && this.isMagicLink) {
      this.previousName =
        window.localStorage.getItem('timeTrackingFormName') || '';
    }
  }

  ngOnDestroy() {
    if (this.trackerComputation) {
      this.trackerComputation.stop();
    }
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  setTimeTrackings(invitee: Invitee) {
    const timeTrackingsFormArray = new FormArray(
      [],
      customValidator.bind(this),
    );
    let timeTrackings = invitee.timeTrackings;
    if (!timeTrackings) {
      this.timeTrackingsFormArray$.next(timeTrackingsFormArray);
      return;
    }
    const timeTrackingsWithFormGroup = timeTrackings.map(
      (timeTracking, index): TimeTrackingWithFormGroup => {
        let from: number, to: number, pause: number;
        if (this.isPooler) {
          from =
            timeTracking.toBeApprovedAt && timeTracking.correctedFrom
              ? timeTracking.correctedFrom
              : timeTracking.from;
          to =
            timeTracking.toBeApprovedAt && timeTracking.correctedTo
              ? timeTracking.correctedTo
              : timeTracking.to;
          pause =
            timeTracking.toBeApprovedAt &&
            (timeTracking.correctedPause || timeTracking.correctedPause === 0)
              ? timeTracking.correctedPause
              : timeTracking.pause;
        } else {
          from = timeTracking.correctedFrom
            ? timeTracking.correctedFrom
            : timeTracking.from;
          to = timeTracking.correctedTo
            ? timeTracking.correctedTo
            : timeTracking.to;
          pause =
            timeTracking.correctedPause || timeTracking.correctedPause === 0
              ? timeTracking.correctedPause
              : timeTracking.pause;
        }

        const formGroup = new FormGroup({
          from: new FormControl(formatTimeNumber(from), Validators.required),
          to: new FormControl(formatTimeNumber(to), Validators.required),
          pause: new FormControl(formatTimeNumber(pause)),
          correctionReason: new FormControl(
            this.isPooler
              ? timeTracking.correctionReason || ''
              : timeTracking.dispoCorrectionReason || '',
          ),
        });
        timeTrackingsFormArray.push(formGroup);
        if (
          (this.isPooler && timeTracking.toBeApprovedAt) ||
          (!this.isPooler && timeTracking.approvedAt)
        ) {
          formGroup.disable();
        } else if (
          !this.isPooler &&
          !this.isMagicLink &&
          !this.permissionsService.completePermissions?.dispoAccess
            .featurePermissions.canApproveHours
        ) {
          // Disable inputs for dispatchers without canApproveHours permission
          formGroup.disable();
        }
        return {
          id: invitee.id + '-' + index,
          timeTracking,
          formGroup,
        };
      },
    );
    this.timeTrackingsFormArray$.next(timeTrackingsFormArray);
    this.timeTrackingsWithFormGroup$.next(timeTrackingsWithFormGroup);
    this.timeTrackingTotal$.next(this.getTimeTrackingTotal());
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  updateTimeValue(
    value: string,
    index: number,
    reference: keyof Pick<
      TimeTracking,
      | 'from'
      | 'correctedFrom'
      | 'to'
      | 'correctedTo'
      | 'pause'
      | 'correctedPause'
    >,
  ) {
    this.hasChanged = true;
    this.ngZone.run(() => {
      const timeTrackingsWithFormGroup = [...this.timeTrackingsWithFormGroup];
      timeTrackingsWithFormGroup[index].timeTracking[reference] =
        Number.parseInt(value.replace(':', ''), 10) || 0;
      this.timeTrackingsWithFormGroup$.next(timeTrackingsWithFormGroup);
      this.timeTrackingTotal$.next(this.getTimeTrackingTotal());
    });
  }

  setToBeApproved(event: Event) {
    event.stopPropagation();
    if (!this.job) return;
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: this.job._id,
        text:
          this.i18n?.transferConfirmationText ||
          'Möchtest du die Stunden wirklich abschliessen? Die Stunden können danach nicht mehr bearbeitet werden.',
        showNameInput: this.isMagicLink && !this.loggedInUser && !this.isPooler,
        nameInput: this.previousName,
        defaultImpolite: true,
      },
    });
    dialogRef.afterClosed().subscribe((result: string) => {
      if (result) {
        let now = getNewUnixTimestamp();
        this.ngZone.run(() => {
          const timeTrackingsWithFormGroup = [
            ...this.timeTrackingsWithFormGroup,
          ];
          timeTrackingsWithFormGroup.forEach((timeTracking) => {
            timeTracking.timeTracking.toBeApprovedAt = now;
            timeTracking.formGroup.disable();
          });
          this.timeTrackingsWithFormGroup$.next(timeTrackingsWithFormGroup);
        });
        this.timeTrackingSubmitted.emit();
        this.saveTimeTracking(result !== this.job?._id ? result : undefined);

        this.toastService.add({ type: 'SUCCESS', message: 'hoursTransferred' });
      }
    });
  }

  setApproved(event: Event) {
    event.stopPropagation();
    if (!this.job) return;
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: this.job._id,
        text:
          this.i18n?.submitConfirmationText ||
          'Möchten Sie die Stunden wirklich Freigeben? Die Stunden können danach nicht mehr bearbeitet werden.',
        showNameInput: this.isMagicLink && !this.loggedInUser,
        nameInput: this.previousName,
        defaultImpolite: true,
      },
    });
    dialogRef.afterClosed().subscribe((result: string) => {
      if (result) {
        if (!this.isPooler) {
          let now = getNewUnixTimestamp();
          this.ngZone.run(() => {
            const timeTrackingsWithFormGroup = [
              ...this.timeTrackingsWithFormGroup,
            ];
            timeTrackingsWithFormGroup.forEach(
              (timeTracking) => (timeTracking.timeTracking.approvedAt = now),
            );
            this.timeTrackingsWithFormGroup$.next(timeTrackingsWithFormGroup);
          });
        }
        this.timeTrackingSubmitted.emit();
        this.saveTimeTracking(result !== this.job?._id ? result : undefined);
        this.toastService.add({ type: 'SUCCESS', message: 'hoursTransferred' });
      }
    });
  }

  saveTimeTracking(validatorName?: string) {
    if (!this.job || !this.invitee) return;
    if (!this.isPooler) {
      if (this.isMagicLink && validatorName) {
        window.localStorage.setItem('timeTrackingFormName', validatorName);
        fetch('https://api.ipify.org?format=json')
          .then((response) => response.json())
          .then((data) => {
            const validatorData: ExternalLinkValidatorData = {
              secret: this.magicSecret || '',
              name: validatorName,
              userAgent: navigator.userAgent,
              ip: data.ip,
            };

            this.timeTrackingService.saveTimeTracking({
              jobId: this.job?._id || '',
              timeTrackings: this.timeTrackingsWithFormGroup.map(
                (t) => t.timeTracking,
              ),
              inviteeId: this.invitee?.id || '',
              userId: this.invitee?.userId || null,
              externalLinkValidatorData: validatorData,
              callback: (error: Meteor.Error) => {
                if (error) {
                  console.error('error: ', error);
                  error;
                  this.toastService.add({
                    type: 'ERROR',
                    message: 'serverError',
                  });
                }
              },
            });
          });
      } else {
        this.timeTrackingService.saveTimeTracking({
          jobId: this.job?._id || '',
          timeTrackings: this.timeTrackingsWithFormGroup.map(
            (t) => t.timeTracking,
          ),
          inviteeId: this.invitee?.id || '',
          userId: this.invitee?.userId || null,
          callback: (error: Meteor.Error) => {
            if (!error) {
              this.toastService.add({ type: 'SUCCESS', message: 'saved' });
            } else {
              console.error('error: ', error);

              this.toastService.add({
                type: 'ERROR',
                message: 'serverError',
              });
            }
          },
        });
      }
    } else {
      this.timeTrackingService.saveTimeTracking({
        jobId: this.job?._id || '',
        timeTrackings: this.timeTrackingsWithFormGroup.map(
          (t) => t.timeTracking,
        ),
        inviteeId: this.invitee?.id || '',
        userId: this.invitee?.userId || null,
        externalLinkValidatorData: this.magicSecret
          ? { secret: this.magicSecret }
          : undefined,
        callback: (error: Meteor.Error) => {
          if (!error) {
            this.toastService.add({ type: 'SUCCESS', message: 'saved' });
          } else {
            console.error('error: ', error);
            this.toastService.add({ type: 'ERROR', message: 'serverError' });
          }
        },
      });
    }
  }

  getTimeTrackingTotal(): number {
    if (!this.job) return 0;
    return getTimeTrackingTotal(
      this.timeTrackingsWithFormGroup.map((t) => t.timeTracking),
      this.job.date,
    );
  }

  trackTimeTracking(_index: number, tracking: TimeTracking): string {
    return tracking.shift.toString();
  }

  generateLink(event: Event) {
    event.stopPropagation();
    if (!this.job || this.isMagicLink || this.isPooler) {
      return;
    }
    let link = `${window.location.origin}/jobs/${this.job._id}/time-tracking/${this.invitee?.customerTimeTrackingSecret}`;
    navigator.clipboard.writeText(link);
    this.toastService.add({ type: 'SUCCESS', message: 'linkCopied' });
  }

  /**
   * Updates the correction reason for a time tracking entry
   */
  updateCorrectionReason(value: string, index: number) {
    const timeTrackingsWithFormGroup = [...this.timeTrackingsWithFormGroup];
    if (this.isPooler) {
      timeTrackingsWithFormGroup[index].timeTracking.correctionReason = value;
    } else {
      timeTrackingsWithFormGroup[index].timeTracking.dispoCorrectionReason =
        value;
    }
    this.timeTrackingsWithFormGroup$.next(timeTrackingsWithFormGroup);
  }

  /**
   * Checks if a time tracking entry has adjustments (corrected values)
   */
  displayCorrectionReason(
    timeTracking: TimeTracking,
    originalShift?: Shift,
  ): boolean {
    const originalTime = {
      from: originalShift?.from,
      to: originalShift?.to,
    };
    const originalPause =
      this.customer?.timeTrackingDefaultPauseInHours?.[timeTracking.shift] || 0;
    const originalPauseMinutes = hoursToMinutes(originalPause);
    const originalPauseMilitaryTime =
      minutesToMilitaryTime(originalPauseMinutes);

    if (this.isPooler) {
      // For pooler: show correction reason if values differ from default OR if corrected values differ from original
      const showReasonForPoolerPauseChange =
        // if the original pause is 0, no reason is needed to enter the pause
        originalPauseMinutes !== 0 &&
        timeTracking.pause !== originalPauseMilitaryTime;
      const showReasonForDispoPauseChange =
        timeTracking.correctedPause !== undefined &&
        timeTracking.correctedPause !== timeTracking.pause;
      return (
        (timeTracking.correctedFrom !== undefined &&
          timeTracking.correctedFrom !== timeTracking.from) ||
        timeTracking.from !== originalTime.from ||
        (timeTracking.correctedTo !== undefined &&
          timeTracking.correctedTo !== timeTracking.to) ||
        timeTracking.to !== originalTime.to ||
        showReasonForPoolerPauseChange ||
        showReasonForDispoPauseChange
      );
    } else {
      // For dispo: only show correction reason if corrected values are different from original values
      return (
        (timeTracking.correctedFrom !== undefined &&
          timeTracking.correctedFrom !== timeTracking.from) ||
        (timeTracking.correctedTo !== undefined &&
          timeTracking.correctedTo !== timeTracking.to) ||
        (timeTracking.correctedPause !== undefined &&
          timeTracking.correctedPause !== timeTracking.pause)
      );
    }
  }
  trackById(_index: number, item: TimeTrackingWithFormGroup) {
    return item.id;
  }

  hasAnyApprovedOrToBeApproved(
    trackings: TimeTrackingWithFormGroup[],
  ): boolean {
    return trackings.some(
      (t) => t.timeTracking.toBeApprovedAt || t.timeTracking.approvedAt,
    );
  }

  openSigningStepper() {
    // For poolers, don't auto-submit - let the stepper handle submission via step 0
    // Only auto-submit for non-poolers if needed (though this shouldn't happen for non-poolers)
    if (
      !this.isPooler &&
      !this.timeTrackingsWithFormGroup.some(
        (item) => item.timeTracking.toBeApprovedAt,
      )
    ) {
      this.ngZone.run(() => {
        this.timeTrackingsWithFormGroup
          .filter((item) => !item.timeTracking.toBeApprovedAt)
          .forEach((item) => {
            item.timeTracking.toBeApprovedAt = getNewUnixTimestamp();
          });
        this.timeTrackingsWithFormGroup$.next(this.timeTrackingsWithFormGroup);
      });
    }

    this.signingStepperOpen = true;
  }

  onTimeTrackingSubmitted(updatedTimeTrackings: TimeTrackingWithFormGroup[]) {
    this.ngZone.run(() => {
      // Update the parent's timeTrackingsWithFormGroup$ with the updated data
      // Sync the timeTracking objects and form group values from the stepper
      const currentTimeTrackings = [...this.timeTrackingsWithFormGroup];
      const syncedTimeTrackings = currentTimeTrackings.map((current, index) => {
        const updated = updatedTimeTrackings[index];
        if (updated) {
          // Update the timeTracking object with the new data from stepper
          const syncedTimeTracking = {
            ...current,
            timeTracking: updated.timeTracking,
          };

          // Sync form group values and disabled state
          const formGroup = syncedTimeTracking.formGroup;
          formGroup.patchValue({
            from: updated.formGroup.controls['from'].value,
            to: updated.formGroup.controls['to'].value,
            pause: updated.formGroup.controls['pause'].value,
            correctionReason:
              updated.formGroup.controls['correctionReason']?.value || '',
          });

          // Sync disabled state
          if (updated.formGroup.disabled && !formGroup.disabled) {
            formGroup.disable();
          } else if (!updated.formGroup.disabled && formGroup.disabled) {
            formGroup.enable();
          }

          return syncedTimeTracking;
        }
        return current;
      });

      this.timeTrackingsWithFormGroup$.next(syncedTimeTrackings);
      // Update the form array to reflect the changes
      this.timeTrackingsFormArray.clear();
      syncedTimeTrackings.forEach((item) => {
        this.timeTrackingsFormArray.push(item.formGroup);
      });
      this.timeTrackingsFormArray$.next(this.timeTrackingsFormArray);
      // Update total
      this.timeTrackingTotal$.next(this.getTimeTrackingTotal());
    });
    this.timeTrackingSubmitted.emit();
  }

  onSignatureSubmitted(event: {
    name: string;
    signatureFile: { fileName: string; fileType: string; fileData: string };
    timeTrackings: TimeTrackingWithFormGroup[];
  }) {
    if (!this.invitee?.customer?.id || !this.job) {
      this.toastService.add({
        type: 'ERROR',
        message: 'missingCustomerId',
      });
      return;
    }

    // Create InviteeFile object
    const inviteeFile = {
      _id: randomId(),
      customerId: this.invitee.customer.id,
      fileName: event.signatureFile.fileName,
      originalName: event.signatureFile.fileName,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      path: randomId(),
      size: Math.round(
        (event.signatureFile.fileData.length * 3) / 4,
      ).toString(), // Approximate size from base64
      type: event.signatureFile.fileType,
      blobString: `data:${event.signatureFile.fileType};base64,${event.signatureFile.fileData}`,
    };

    // Save the signature file
    Meteor.call(
      'upsertInviteeFile',
      inviteeFile,
      true,
      (error: Meteor.Error, result: any) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: 'serverError',
          });
          console.error('Error saving signature:', error);
          return;
        }
        if (result) {
          const fileReference = {
            id: result._id,
            fileName: result.fileName,
            path: result.path,
            size: result.size,
            type: result.type,
          };
          this.ngZone.run(() => {
            const updatedTimeTrackings = event.timeTrackings.map((item) => ({
              ...item,
              timeTracking: {
                ...item.timeTracking,
                signatureFile: fileReference,
                signatureName: event.name,
              },
            }));
            this.timeTrackingsWithFormGroup$.next(updatedTimeTrackings);
          });

          this.timeTrackingSubmitted.emit();
          this.saveTimeTracking();
        }
      },
    );
  }
}
